import React from 'react';
import { InvoiceStatus } from '../../../domain/types';
import { Invoice } from '../../../providers/api/dtos';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

export type UseComputedInvoiceStatusResult = {
  status: InvoiceStatus,
  canBeUpdated: boolean,
  canBeUpdatedTo: (status: InvoiceStatus) => boolean,
};

export const computedInvoiceStatusCanBeUpdatedTo: Record<InvoiceStatus, InvoiceStatus[]> = {
  [InvoiceStatus.DRAFT]: [InvoiceStatus.PROFORMA, InvoiceStatus.BILLED, InvoiceStatus.PAID, InvoiceStatus.COLLECTED],
  [InvoiceStatus.PROFORMA]: [InvoiceStatus.BILLED, InvoiceStatus.PAID, InvoiceStatus.NOT_PAID, InvoiceStatus.COLLECTED],
  [InvoiceStatus.BILLED]: [InvoiceStatus.PAID, InvoiceStatus.NOT_PAID, InvoiceStatus.COLLECTED],
  [InvoiceStatus.PAID]: [InvoiceStatus.COLLECTED],
  [InvoiceStatus.NOT_PAID]: [],
  [InvoiceStatus.COLLECTED]: [],
};

export const useComputedInvoiceStatus = ({ status }: Invoice): UseComputedInvoiceStatusResult => {
  const { userIsAdmin, userIsFinance } = useRBAC();

  const canBeUpdatedTo = React.useMemo(() => {
    if (userIsAdmin || userIsFinance) {
      return computedInvoiceStatusCanBeUpdatedTo[status];
    }

    return [];
  }, [status, userIsAdmin, userIsFinance]);

  return React.useMemo(() => ({
    status,
    canBeUpdated: canBeUpdatedTo.length > 0,
    canBeUpdatedTo: (s: InvoiceStatus) => canBeUpdatedTo.includes(s),
  }), [canBeUpdatedTo, status]);
};
